import { c as _c } from "react/compiler-runtime";
import { type MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { type Message } from '../../MessageCard/types';
import { countNewMessages, findNewlySentUserMessage, getFlexGapPx, getLastUserMessageIndex, getPinAvailableHeight, getPinnedUserMessageScrollTop, getPinSpacerHeight, getPixelValue, getScrollableAncestor, isContentOverflowingScrollport, setPinSpacerHeight } from '../utils';
interface UseMessageListScrollOptions {
  messages: Message[];
  isLoading?: boolean;
  autoScrollToBottom: boolean;
  pinLatestUserMessageToTop: boolean;
}
interface UseMessageListScrollResult {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  lastUserMessageRef: MutableRefObject<HTMLDivElement | null>;
  setLastUserMessageRef: (node: HTMLDivElement | null) => void;
  spacerRef: MutableRefObject<HTMLDivElement | null>;
  lastUserMessageIndex: number;
}
const MAX_NEW_MESSAGES_FOR_PIN = 2;
const resetPinStateWhenDisabled = ({
  spacer,
  messages,
  isLoading,
  prevMessageIdsRef,
  prevIsLoadingRef,
  lastPinnedUserMessageIdRef
}: {
  spacer: HTMLDivElement | null;
  messages: Message[];
  isLoading: boolean | undefined;
  prevMessageIdsRef: MutableRefObject<Set<string>>;
  prevIsLoadingRef: MutableRefObject<boolean | undefined>;
  lastPinnedUserMessageIdRef: MutableRefObject<string | null>;
}): void => {
  if (spacer != null) {
    setPinSpacerHeight(spacer, 0);
  }
  prevMessageIdsRef.current = new Set(messages.map(message => message.id));
  prevIsLoadingRef.current = isLoading;
  lastPinnedUserMessageIdRef.current = null;
};
const handleFirstPinnedRenderScroll = ({
  isFirstRender,
  messages,
  isMessageContentOverflowing,
  scrollToBottom
}: {
  isFirstRender: MutableRefObject<boolean>;
  messages: Message[];
  isMessageContentOverflowing: () => boolean;
  scrollToBottom: () => void;
}): boolean => {
  if (!isFirstRender.current) {
    return false;
  }
  isFirstRender.current = false;
  if (messages.length > 0 && isMessageContentOverflowing()) {
    scrollToBottom();
  }
  return true;
};
const handleNewlySentUserMessagePin = ({
  newlySentUserMessage,
  newMessageCount,
  lastPinnedUserMessageIdRef,
  updatePinSpacer,
  scrollLastUserMessageToTop
}: {
  newlySentUserMessage: Message | null;
  newMessageCount: number;
  lastPinnedUserMessageIdRef: MutableRefObject<string | null>;
  updatePinSpacer: () => void;
  scrollLastUserMessageToTop: () => void;
}): boolean => {
  // User just sent (optionally with a pending copilot bubble in the same update).
  // Pin at most once per user message id — streaming updates must not re-scroll.
  if (newlySentUserMessage == null || newMessageCount > MAX_NEW_MESSAGES_FOR_PIN) {
    return false;
  }
  updatePinSpacer();
  if (lastPinnedUserMessageIdRef.current !== newlySentUserMessage.id) {
    lastPinnedUserMessageIdRef.current = newlySentUserMessage.id;
    scrollLastUserMessageToTop();
  }
  return true;
};
const handleLoadingFinishedScroll = ({
  wasLoading,
  isLoading,
  updatePinSpacer,
  isMessageContentOverflowing,
  scrollToBottom
}: {
  wasLoading: boolean | undefined;
  isLoading: boolean | undefined;
  updatePinSpacer: () => void;
  isMessageContentOverflowing: () => boolean;
  scrollToBottom: () => void;
}): boolean => {
  if (wasLoading !== true || isLoading === true) {
    return false;
  }
  updatePinSpacer();
  if (isMessageContentOverflowing()) {
    scrollToBottom();
  }
  return true;
};
export const useMessageListScroll = t0 => {
  const $ = _c(32);
  const {
    messages,
    isLoading,
    autoScrollToBottom,
    pinLatestUserMessageToTop
  } = t0;
  const containerRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const spacerRef = useRef(null);
  const isFirstRender = useRef(true);
  let t1;
  if ($[0] !== messages) {
    t1 = new Set(messages.map(_temp));
    $[0] = messages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const prevMessageIdsRef = useRef(t1);
  const prevIsLoadingRef = useRef(isLoading);
  const lastPinnedUserMessageIdRef = useRef(null);
  let t2;
  if ($[2] !== messages || $[3] !== pinLatestUserMessageToTop) {
    t2 = pinLatestUserMessageToTop ? getLastUserMessageIndex(messages) : -1;
    $[2] = messages;
    $[3] = pinLatestUserMessageToTop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const lastUserMessageIndex = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = node => {
      if (node != null) {
        lastUserMessageRef.current = node;
      }
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const setLastUserMessageRef = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const scrollableParent = getScrollableAncestor(containerRef.current);
      if (scrollableParent == null) {
        return;
      }
      scrollableParent.scrollTo({
        top: scrollableParent.scrollHeight,
        behavior: "smooth"
      });
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const scrollToBottom = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const container = containerRef.current;
      const scrollableParent_0 = getScrollableAncestor(container);
      if (container == null || scrollableParent_0 == null) {
        return false;
      }
      const spacer = spacerRef.current;
      const spacerInFlow = spacer != null && getComputedStyle(spacer).display !== "none";
      const spacerHeight = spacerInFlow ? (spacer?.offsetHeight ?? 0) + getFlexGapPx(container) : 0;
      const scrollPaddingTop = getPixelValue(getComputedStyle(scrollableParent_0).scrollPaddingTop);
      return isContentOverflowingScrollport(container.scrollHeight - spacerHeight, scrollableParent_0.clientHeight, scrollPaddingTop);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const isMessageContentOverflowing = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const spacer_0 = spacerRef.current;
      const userMessage = lastUserMessageRef.current;
      const container_0 = containerRef.current;
      const scrollableParent_1 = getScrollableAncestor(container_0);
      if (spacer_0 == null) {
        return;
      }
      if (userMessage == null || container_0 == null || scrollableParent_1 == null) {
        setPinSpacerHeight(spacer_0, 0);
        return;
      }
      const gap = getFlexGapPx(container_0);
      let contentAfterUser = 0;
      let siblingCountAfterUser = 0;
      let sibling = userMessage.nextElementSibling;
      while (sibling != null) {
        if (sibling !== spacer_0) {
          contentAfterUser = contentAfterUser + (sibling as HTMLElement).offsetHeight;
          siblingCountAfterUser = siblingCountAfterUser + 1;
        }
        sibling = sibling.nextElementSibling;
      }
      const {
        paddingTop,
        paddingBottom
      } = getComputedStyle(container_0);
      const verticalPadding = getPixelValue(paddingTop) + getPixelValue(paddingBottom);
      const scrollPaddingTop_0 = getPixelValue(getComputedStyle(scrollableParent_1).scrollPaddingTop);
      setPinSpacerHeight(spacer_0, getPinSpacerHeight({
        availableHeight: getPinAvailableHeight(scrollableParent_1.clientHeight, scrollPaddingTop_0),
        userMessageHeight: userMessage.offsetHeight,
        contentAfterUserHeight: contentAfterUser,
        siblingCountAfterUser,
        gap,
        verticalPadding
      }));
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const updatePinSpacer = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const userMessage_0 = lastUserMessageRef.current;
      const scrollableParent_2 = getScrollableAncestor(containerRef.current);
      if (userMessage_0 == null || scrollableParent_2 == null) {
        return;
      }
      const userTop = userMessage_0.getBoundingClientRect().top;
      const parentTop = scrollableParent_2.getBoundingClientRect().top;
      const scrollPaddingTop_1 = getPixelValue(getComputedStyle(scrollableParent_2).scrollPaddingTop);
      scrollableParent_2.scrollTo({
        top: getPinnedUserMessageScrollTop({
          userTop,
          parentTop,
          scrollTop: scrollableParent_2.scrollTop,
          scrollPaddingTop: scrollPaddingTop_1
        }),
        behavior: "smooth"
      });
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const scrollLastUserMessageToTop = t7;
  let t8;
  let t9;
  if ($[10] !== isLoading || $[11] !== lastUserMessageIndex || $[12] !== messages || $[13] !== pinLatestUserMessageToTop) {
    t8 = () => {
      if (!pinLatestUserMessageToTop) {
        resetPinStateWhenDisabled({
          spacer: spacerRef.current,
          messages,
          isLoading,
          prevMessageIdsRef,
          prevIsLoadingRef,
          lastPinnedUserMessageIdRef
        });
        return;
      }
      if (lastUserMessageIndex < 0) {
        lastUserMessageRef.current = null;
      }
      updatePinSpacer();
      const previousIds = prevMessageIdsRef.current;
      const newlySentUserMessage = findNewlySentUserMessage(messages, previousIds);
      const newMessageCount = countNewMessages(messages, previousIds);
      const wasLoading = prevIsLoadingRef.current;
      prevMessageIdsRef.current = new Set(messages.map(_temp2));
      prevIsLoadingRef.current = isLoading;
      if (handleFirstPinnedRenderScroll({
        isFirstRender,
        messages,
        isMessageContentOverflowing,
        scrollToBottom
      })) {
        return;
      }
      if (handleNewlySentUserMessagePin({
        newlySentUserMessage,
        newMessageCount,
        lastPinnedUserMessageIdRef,
        updatePinSpacer,
        scrollLastUserMessageToTop
      })) {
        return;
      }
      if (handleLoadingFinishedScroll({
        wasLoading,
        isLoading,
        updatePinSpacer,
        isMessageContentOverflowing,
        scrollToBottom
      })) {
        return;
      }
      if (newMessageCount > MAX_NEW_MESSAGES_FOR_PIN && isMessageContentOverflowing()) {
        scrollToBottom();
      }
    };
    t9 = [messages, isLoading, lastUserMessageIndex, pinLatestUserMessageToTop, isMessageContentOverflowing, scrollLastUserMessageToTop, scrollToBottom, updatePinSpacer];
    $[10] = isLoading;
    $[11] = lastUserMessageIndex;
    $[12] = messages;
    $[13] = pinLatestUserMessageToTop;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[16] !== pinLatestUserMessageToTop) {
    t10 = () => {
      if (!pinLatestUserMessageToTop) {
        return;
      }
      const scrollableParent_3 = getScrollableAncestor(containerRef.current);
      if (scrollableParent_3 == null) {
        return;
      }
      const resizeObserver = new ResizeObserver(() => {
        updatePinSpacer();
      });
      resizeObserver.observe(scrollableParent_3);
      if (lastUserMessageRef.current != null) {
        resizeObserver.observe(lastUserMessageRef.current);
      }
      return () => {
        resizeObserver.disconnect();
      };
    };
    $[16] = pinLatestUserMessageToTop;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== lastUserMessageIndex || $[19] !== messages.length || $[20] !== pinLatestUserMessageToTop) {
    t11 = [messages.length, lastUserMessageIndex, pinLatestUserMessageToTop, updatePinSpacer];
    $[18] = lastUserMessageIndex;
    $[19] = messages.length;
    $[20] = pinLatestUserMessageToTop;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  useEffect(t10, t11);
  let t12;
  if ($[22] !== autoScrollToBottom || $[23] !== pinLatestUserMessageToTop) {
    t12 = () => {
      if (pinLatestUserMessageToTop || !autoScrollToBottom) {
        return;
      }
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      scrollToBottom();
    };
    $[22] = autoScrollToBottom;
    $[23] = pinLatestUserMessageToTop;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== autoScrollToBottom || $[26] !== isLoading || $[27] !== messages.length || $[28] !== pinLatestUserMessageToTop) {
    t13 = [messages.length, isLoading, autoScrollToBottom, pinLatestUserMessageToTop, scrollToBottom];
    $[25] = autoScrollToBottom;
    $[26] = isLoading;
    $[27] = messages.length;
    $[28] = pinLatestUserMessageToTop;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== lastUserMessageIndex) {
    t14 = {
      containerRef,
      lastUserMessageRef,
      setLastUserMessageRef,
      spacerRef,
      lastUserMessageIndex
    };
    $[30] = lastUserMessageIndex;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
function _temp(message) {
  return message.id;
}
function _temp2(message_0) {
  return message_0.id;
}